import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, CardHeader, CardContent, Chip, Box, IconButton, Typography, TextField, Button, ListItemButton, ListItemIcon, ListItemText, Tooltip, CardActions } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import moment from 'moment'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { useCurrentMoment } from 'hooks/time'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useBoolean } from 'hooks/state'
import { useSnackbar } from 'hooks/redux'
import { useContactDescription } from 'hooks/chat'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { startCase } from 'lodash'
import { ChatThreadItem_Contact$key } from '__generated__/ChatThreadItem_Contact.graphql'
import ChatMessageContents from 'components/ChatMessageContents'
import RecipientAvatar from 'components/RecipientAvatar'
import { ChatThreadItem_ChatMessage$key } from '__generated__/ChatThreadItem_ChatMessage.graphql'
import { ChatThreadItem_UpdateChatMessageMutation } from '__generated__/ChatThreadItem_UpdateChatMessageMutation.graphql'
import EditIcon from '@mui/icons-material/Edit'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckIcon from '@mui/icons-material/Check'
import { ChatThreadItem_DeleteChatMessageMutation } from '__generated__/ChatThreadItem_DeleteChatMessageMutation.graphql'
import Suspense from 'components/Suspense'
import MoreButtonMenu from 'components/MoreButtonMenu'
import DeleteIcon from '@mui/icons-material/Delete'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { formatAsDateTime } from 'services/formatting'
import { Search, Settings } from '@mui/icons-material'
import { preloadableLazy } from 'services/react'
import { Nullable } from 'types'
import router from 'next/router'
import AutomationChatLink from './components/AutomationChatLink'

const AIEvaluationDialog = preloadableLazy(() => import('components/AIEvaluationDialog'))

interface IChatThreadItemProps {
  createdAt?: Nullable<string>
  label?: Nullable<string>
  message?: string
  contactFragmentRef?: ChatThreadItem_Contact$key
  children?: any
  chatMessageFragmentRef?: ChatThreadItem_ChatMessage$key
}

function ChatThreadItem(props: IChatThreadItemProps) {
  const { contactFragmentRef, chatMessageFragmentRef, message, createdAt, label, children } = props
  const ref = useRef(null)
  const theme = useTheme()
  const [editing, setEditing] = useState(false)
  const isSmallScreen = useMediaQuery(() => theme.breakpoints.down('sm'))
  const { showSnackbarMessage } = useSnackbar()

  const [textValue, setTextValue] = useState(message)

  const chatMessage = useFragment(graphql`
    fragment ChatThreadItem_ChatMessage on ChatMessage {
      id
      deletedAt
      editedAt
      label
      generatedByAi

      jobApplicationEvaluation {
        id
        jobApplication {
          job {
            slug
          }
        }
      }

      sender {
        ... on User {
          self
          firstName
        }
      }
    }
  `, chatMessageFragmentRef)

  const { execute: executeUpdateChatMessage, loading: isUpdatingMessage } = useQuickCommit<ChatThreadItem_UpdateChatMessageMutation>(graphql`
    mutation ChatThreadItem_UpdateChatMessageMutation($input: UpdateChatMessageInput!) {
      updateChatMessage(input: $input) {
        chatMessage {
          id
          editedAt
          message
        }
      }
    }
  `)

  const { execute: executeDeleteChatMessage, loading: isDeletingMessage } = useQuickCommit<ChatThreadItem_DeleteChatMessageMutation>(graphql`
    mutation ChatThreadItem_DeleteChatMessageMutation($input: DeleteChatMessageInput!) {
      deleteChatMessage(input: $input) {
        chatMessage {
          id
          deletedAt
        }
      }
    }
  `)

  const deleteMessage = useCallback(async () => {
    if (chatMessage?.id) {
      const result = await executeDeleteChatMessage({ input: { chatMessageId: chatMessage.id } })

      if (result?.deleteChatMessage?.chatMessage?.deletedAt) {
        showSnackbarMessage('Message Deleted', { level: 'success' })
      }
    }
  }, [chatMessage?.id, executeDeleteChatMessage])

  const updateMessage = useCallback(async () => {
    if (chatMessage?.id) {
      const result = await executeUpdateChatMessage({ input: { chatMessageId: chatMessage.id, message: textValue } })

      if (result?.updateChatMessage?.chatMessage?.message) {
        showSnackbarMessage('Message Updated', { level: 'success' })
        setEditing(false)
      }
    }
  }, [chatMessage?.id, executeUpdateChatMessage, textValue])

  const contact = useFragment(graphql`
    fragment ChatThreadItem_Contact on Recipient {
      __typename

      ... on User {
        name
      }
      
      ... on ChatBot {
        name
      }

      ...RecipientAvatar_Recipient
      ...chat_useContactDescription
    }
  `, contactFragmentRef)

  const currentMoment = useCurrentMoment()
  const messageSubheader = useMemo(() => {
    if (!createdAt) return null

    let messageCreatedAt = moment(createdAt)
    if (messageCreatedAt.isAfter()) {
      messageCreatedAt = moment()
    }
    return messageCreatedAt.fromNow()
  }, [currentMoment])

  const contactSubheader = useContactDescription(contact)

  const subheader = useMemo(() => {
    let baseSubheader = messageSubheader || contactSubheader
    const timestamp = chatMessage?.editedAt ? `${baseSubheader} - edited` : baseSubheader
    return (
      <div>
        <Tooltip title={formatAsDateTime(moment(createdAt))}>
          <span>{(timestamp)}</span>
        </Tooltip>
      </div>
    )
  }, [messageSubheader, contactSubheader, chatMessage?.editedAt, createdAt])

  const chipContent = useMemo(() => {
    if (label === 'contract_introduction') return 'Automated Message'
    if (label === 'post_interview_reminder') return 'Automated Message'
    if (label) return startCase(label)
    if (contact?.__typename === 'ChatBot') return 'AI Bot'

    return null
  }, [message])

  const evaluationDialogState = useBoolean()
  const showAIJobApplicationEvaluation = Boolean(chatMessage?.generatedByAi && chatMessage?.jobApplicationEvaluation?.id)

  useEffect(() => {
    if (showAIJobApplicationEvaluation) {
      AIEvaluationDialog.preload()
    }
  }, [showAIJobApplicationEvaluation])

  const loading = isUpdatingMessage || isDeletingMessage

  if (!message && !chatMessage?.deletedAt) return null

  return (
    <Box position="relative">
      {(chatMessage?.deletedAt) && (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: theme.spacing(2) }}>
          {chatMessage?.sender?.firstName || 'Someone'} deleted a message
        </Typography>
      )}

      {(!chatMessage?.deletedAt) && (
      <React.Fragment>
        <Card variant="outlined" ref={ref} data-cy="chat-thread-item" data-testid="chat-thread-item" style={{ marginTop: theme.spacing(2) }}>
          <CardHeader
            avatar={(
              <RecipientAvatar
                recipientFragmentRef={contact}
              />
            )}
            title={(
              <React.Fragment>
                {contact?.name || 'Unnamed User'}
                {chipContent && <Chip size="small" color="primary" label={chipContent} style={{ marginLeft: theme.spacing(1) }} />}
                {chatMessage?.id && (
                  <Suspense>
                    <AutomationChatLink
                      chatMessageId={chatMessage?.id}
                      style={{ marginLeft: theme.spacing(1) }}
                    />
                  </Suspense>
                )}
              </React.Fragment>
            )}
            subheader={subheader}
          />

          {!editing ? (
            message && <CardContent style={{ paddingTop: 0, paddingBottom: 0 }}><ChatMessageContents message={message} /></CardContent>
          ) : (
            <Box position="relative">
              <TextField
                multiline
                rows={5}
                style={{ width: '85%' }}
                defaultValue={message}
                variant="outlined"
                onChange={event => setTextValue(event.target.value)}
                disabled={loading}
              />

              {isSmallScreen ? (
                <React.Fragment>
                  <IconButton style={{ position: 'absolute', bottom: 0, right: 26 }} onClick={() => updateMessage()} disabled={loading}>
                    <CheckIcon />
                  </IconButton>

                  <IconButton style={{ position: 'absolute', bottom: -1, right: 0 }} onClick={() => setEditing(false)} disabled={loading}>
                    <CancelIcon />
                  </IconButton>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Button style={{ position: 'absolute', bottom: 0, right: 0, width: '14%' }} color="primary" onClick={() => updateMessage()} disabled={loading}>
                    Confirm
                  </Button>

                  <Button style={{ position: 'absolute', bottom: 40, right: 0, width: '14%' }} onClick={() => setEditing(false)} disabled={loading}>
                    Cancel
                  </Button>
                </React.Fragment>
              )}
            </Box>
          )}

          {children}

          {(chatMessage?.jobApplicationEvaluation?.id && showAIJobApplicationEvaluation) && (
            <CardActions>
              <Button
                href={`/hire/jobs/${chatMessage?.jobApplicationEvaluation?.jobApplication?.job?.slug}/screening#ai-recruiter`}
                startIcon={<Settings />}
                style={{ marginLeft: 'auto' }}
                color="secondary"
                onClick={(event) => {
                  event.preventDefault()
                  router.push(`/hire/jobs/${chatMessage?.jobApplicationEvaluation?.jobApplication?.job?.slug}/screening#ai-recruiter`)
                }}
              >
                Customize
              </Button>
              <Button startIcon={<Search />} onClick={evaluationDialogState.setTrue} color="secondary">
                More Details
              </Button>
              {evaluationDialogState.value && (
                <Suspense noSsr>
                  <AIEvaluationDialog
                    jobApplicationEvaluationId={chatMessage?.jobApplicationEvaluation?.id}
                    onClose={evaluationDialogState.setFalse}
                    open={evaluationDialogState.value}
                  />
                </Suspense>
              )}
            </CardActions>
          )}
        </Card>

        {(chatMessage?.sender?.self && chatMessage?.id) && (
          <MoreButtonMenu
            style={{ position: 'absolute', top: 6, right: 6 }}
            icon={<MoreHorizIcon style={{ marginRight: 0 }} />}
            iconOnly
            MenuListProps={{ dense: true }}
          >
            <ListItemButton
              onClick={() => setEditing(true)}
            >
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>

              <ListItemText primary="Edit Message" />
            </ListItemButton>

            <ListItemButton
              onClick={deleteMessage}
            >
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>

              <ListItemText primary="Delete Message" />
            </ListItemButton>
          </MoreButtonMenu>
        )}
      </React.Fragment>
      )}
    </Box>
  )
}

export default memo(ChatThreadItem)
